import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { some, head, isEmpty } from 'lodash'
import { toast } from 'react-toastify'

import Button from '../Button'
import RadioSelectionModal from '../RadioSelectionModal'

import PeripheralBridge from '../../utils/peripheralBridge'
import { createPrinterData } from '../../utils/printing'
import { PRINTING_ENABLED } from '../../constants'
import styles from './ReceiptMenu.module.scss'

const ReceiptMenu = ({
  className,
  customerEmail,
  deviceReceiptPrinterUuid,
  deviceSubtype,
  menu,
  onDone,
  onMount,
  order,
  paymentId,
  printers,
  printOrder,
  sendEmails,
  serviceChargeLegal,
  showEmail,
}) => {
  const mediaQuery = window.matchMedia("(min-width: 800px)")

  const classNames = cn(styles.receiptMenu, className)
  const [emailInfo, setEmailInfo] = useState({ emails: customerEmail, isFocused: false, isValid: true })
  const [showPrinterSelection, setShowPrinterSelection] = useState(false)

  const onEmailSubmit = () => {
    const emails = emailInfo.emails.replace(/\s/g, "").split(",")
    const emailRegex = /.+@.+\.+.+/i;
    const emailsAreValid = !(some(emails, (email) => !emailRegex.test(email))) || !emailInfo.emails.length

    if (emailsAreValid && head(emails).length) {
      sendEmails(order.uuid, emails, paymentId)
    } else {
      toast.error("Can't send email.")
    }
    setEmailInfo({ ...emailInfo, isValid: emailsAreValid })
  }

  const additionalEmailStyles = {
    title: {},
    input: {}
  }

  if (emailInfo.isFocused) {
    additionalEmailStyles.input.border = 'solid 2px #17a141'
    additionalEmailStyles.title.color = '#17a141'
  } else if (!emailInfo.isValid) {
    additionalEmailStyles.input.border = 'solid 2px #e21e5c'
    additionalEmailStyles.title.color = '#e21e5c'
  }

  const exclamation = !emailInfo.isValid && (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
    >
      <path
        fill="#E21E5C"
        fillRule="nonzero"
        d="M16 32c8.832 0 16-7.168 16-16S24.832 0 16 0 0 7.168 0 16s7.168 16 16 16zM14.4 8h3.2v9.6h-3.2V8zm0 12.8h3.2V24h-3.2v-3.2z"
      />
    </svg>
  )

  const sendEmailButton = emailInfo.isValid && (
    <div className={styles.emailSubmit} onClick={onEmailSubmit}>SEND</div>
  )

  const emailAccessory = emailInfo.isValid ? sendEmailButton : exclamation

  const printReceipt = () => {
    if (!deviceReceiptPrinterUuid) {
      setShowPrinterSelection(!showPrinterSelection)
    } else {
      printOrder({
        printType: 'suite-receipt',
        printerUuids: [deviceReceiptPrinterUuid],
        orderUuid: order?.uuid,
        paymentUuid: paymentId,
      })
    }
  }

  useEffect(() => {
    onMount()
  }, [onMount])

  const printReceiptButton = PRINTING_ENABLED ? (
    <div className={styles.printButtonContainer}>
      <Button
        className={styles.printReceiptButton}
        onClick={printReceipt}
        title="Print Receipt"
      >
        PRINT RECEIPT
      </Button>
    </div>
  ) : null

  let emailMessage = <p className={styles.emailMessage}>Separate multiple emails with comma</p>
  if (!emailInfo.isValid) emailMessage = <p className={styles.emailError}>One or more email addresses are not formatted correctly</p>

  const emailComponent = showEmail && (
    <div className={styles.email}>
      <p className={styles.emailTitle} style={additionalEmailStyles.title}>Email my receipt</p>
      <div className={styles.emailInputContainer} style={additionalEmailStyles.input}>
        <input
          className={styles.emailInput}
          value={emailInfo.emails}
          type="text"
          autoComplete="off"
          onBlur={() => {
            setEmailInfo({ ...emailInfo, isFocused: false })
          }}
          onFocus={() => {
            setEmailInfo({ ...emailInfo, isFocused: true, isValid: true })
          }}
          onChange={e =>
            setEmailInfo({ ...emailInfo, emails: e.target.value })
          }
        />
        {emailAccessory}
      </div>
      {emailMessage}
    </div>
  )

  const renderHeaderPrompt = () => {
    return showEmail
      ? "Thanks! Enter your email for a copy of your receipt."
      : "Thanks! Take a picture of the screen if you'd like a digital copy of your receipt."
  }

  const printerSelection = (
    <RadioSelectionModal
      show={showPrinterSelection}
      toggleShow={setShowPrinterSelection}
      items={printers}
      title={"Print Receipt"}
      selectionTitle={"Select a printer"}
      buttonText={"PRINT"}
      className={className}
      onDone={(printerSelected) =>
        printOrder({
          printType: "suite-receipt",
          printerUuids: [printerSelected?.uuid],
          orderUuid: order?.uuid,
          paymentUuid: paymentId,
        })
      }
    />
  )

  return (
    <div className={classNames}>
      <div className={styles.menuContainer}>
        {mediaQuery.matches && (
          <>
            <div className={styles.message}>{renderHeaderPrompt()}</div>
          </>
        )}
        {emailComponent}
        {printReceiptButton}
        <Button className={styles.doneButton} onClick={() => onDone(order?.uuid)}>Done</Button>
        <p className={styles.legal}>{serviceChargeLegal}</p>
      </div>
      {printerSelection}
    </div>
  )
}

ReceiptMenu.defaultProps = {
  customerEmail: '',
  deviceSubtype: '',
  menu: {},
  order: {},
  sendEmails: () => {},
  showEmail: false,
}

ReceiptMenu.propTypes = {
  customerEmail: PropTypes.string,
  deviceSubtype: PropTypes.string,
  menu: PropTypes.object,
  order: PropTypes.object,
  sendEmails: PropTypes.func,
  showEmail: PropTypes.bool,
}

export default ReceiptMenu
